import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, Target, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const PresidentPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  // Helper function to remove quotes and bold important words
  const formatText = (text) => {
    if (!text) return '';
    
    // Remove quotes
    let formatted = text.replace(/[""]/g, '').replace(/['']/g, '');
    
    // Important words to bold
    const importantWords = [
      'Industrial Federation', 'Industries', 'Industry', 'Government', 
      'Maharashtra', 'Sustainable', 'Innovation', 'Compliance', 'Quality',
      'Safety', 'Environmental', 'Economic Growth', 'Platform', 'Empower',
      'Challenges', 'Solutions', 'Collaboration', 'Development', 'Progress',
      'Pollution Control', 'DISH', 'MIDC', 'MSEDCL', 'DIC', 'Labour Department'
    ];
    
    // Bold important words
    importantWords.forEach(word => {
      const regex = new RegExp(`\\b(${word})\\b`, 'gi');
      formatted = formatted.replace(regex, '<strong>$1</strong>');
    });
    
    return DOMPurify.sanitize(formatted);
  };

  // Get commitment items from dataSync
  const getCommitmentItems = () => {
    return [
      getText('about.president.focus.sustainable.desc') || getText('about.president.commitment.sustainable') || "Promoting sustainable and responsible industrial growth",
      getText('about.president.commitment.safeWorkplaces'),
      getText('about.president.commitment.resourceManagement'),
      getText('about.president.commitment.collectiveVoice'),
      getText('about.president.commitment.innovationEcosystem')
    ];
  };

  const commitmentItems = getCommitmentItems();

  // Get timeline data from dataSync
  const getTimelineData = () => {
    return [
      {
        year: getText('about.president.timeline.2020.title') || "2020 - Present",
        title: getText('about.president.timeline.2020.position') || "President, IFM",
        desc: getText('about.president.timeline.2020.desc') || "Leading industrial transformation across Maharashtra"
      },
      {
        year: getText('about.president.timeline.2015.title') || "2015 - 2020",
        title: getText('about.president.timeline.2015.position') || "Former Director, Maharashtra Industries",
        desc: getText('about.president.timeline.2015.desc') || "Oversaw industrial policy implementation and development"
      },
      {
        year: getText('about.president.timeline.2010.title') || "2010 - 2015",
        title: getText('about.president.timeline.2010.position') || "Industry Advisor, Govt. of Maharashtra",
        desc: getText('about.president.timeline.2010.desc') || "Provided strategic guidance for industrial growth initiatives"
      }
    ];
  };

  const timelineData = getTimelineData();

  // Get government departments
  const governmentDepts = [
    { name: 'Pollution Control', icon: '🌱' },
    { name: 'DISH', icon: '🏭' },
    { name: 'MIDC', icon: '🏗️' },
    { name: 'MSEDCL', icon: '⚡' },
    { name: 'DIC', icon: '📊' },
    { name: 'Labour Dept', icon: '👷' },
    { name: 'Industry Dept', icon: '🏢' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100/50 to-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-white/5 rounded-full blur-2xl transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* About Us Button - Top */}
            <div className="mb-4">
              <Link 
                to="/about" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>
            
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight drop-shadow-lg">
                {getText('home.president.title') || getText('about.president.message.title') || "President's Message"}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium drop-shadow-md">
                {getText('about.president.organization') || "Industrial Federation"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
   <section className="py-12 md:py-16 relative bg-background-light">
        {/* President Profile - Full Width Section */}
  <div className="w-full mb-12 relative z-10">
    <div className="bg-white border-y border-navy-deep/10 overflow-hidden">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">

                {/* Image Container */}
          <div className="flex-shrink-0">
            <div className="relative" style={{ maxWidth: '200px' }}>
              <div className="rounded-xl overflow-hidden border-4 border-white shadow-2xl">
                      <img 
                        src="/images/4.jpg"
                        alt={getText('home.president.name') || 'President'}
                  className="w-full rounded-lg object-cover"
                        style={{ aspectRatio: '3/4', maxHeight: '250px' }}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&auto=format&fit=crop';
                        }}
                      />
                    </div>
                  </div>
                </div>
                
          {/* Name and Title */}
                <div className="flex-1 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">
                    {getText('home.president.name') || 'Mr Kailas Narke'}
                  </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-4 rounded-full"></div>
            <p className="text-lg md:text-xl text-navy-deep/70 font-semibold">
              {getText('home.president.position') || 'President, Industrial Federation of Maharashtra Region'}
                  </p>
            <p className="text-sm md:text-base text-navy-deep/60 font-bold mt-2">
              Director - SSDK Project Solutions Pvt Ltd
            </p>
                </div>

              </div>
            </div>
          </div>
        </div>
        
        <div className="container max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">

          <div className="w-full">

              {/* Message Container */}
      <div className="bg-white rounded-xl border border-navy-deep/10 mb-10 overflow-hidden shadow-sm">
        
                {/* Decorative Top Border */}
        <div className="h-1 bg-primary"></div>
        
        <div className="p-6 md:p-8 lg:p-10">
                
                  {/* Header Section */}
          <div className="mb-8 pb-6 border-b border-navy-deep/10">
                    <div className="flex items-center gap-4 mb-3">
              <div className="bg-primary p-3 rounded-lg text-white">
                <Target className="h-6 w-6" />
                      </div>
                      <div>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-deep mb-1">
                          {getText('home.president.title') || getText('about.president.message.title') || "President's Message"}
                        </h2>
                <p className="text-base md:text-lg text-navy-deep/60 font-medium">
                          {getText('about.president.organization') || "Industrial Federation"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message Content */}
          <div className="space-y-6 serif-text text-lg leading-[1.8] text-navy-deep/80">
            
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message1') || "Dear Industrial Partners,") }} />
                    
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message2')) }} />
                    
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message2')) }} />
                    
                    {/* Mission Statement Box */}
                    <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white p-5 md:p-6 my-5 rounded-xl shadow-lg border border-primary-500/50 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2"></div>
                      <div className="relative z-10">
                        <p className="text-base font-semibold mb-2">{getText('home.president.missionTitle') || 'Our Mission is Simple Yet Powerful'}</p>
                        <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message3') || "To empower industries, resolve challenges, and serve as a strong and effective bridge between industries and government authorities.") }} />
                      </div>
                    </div>
                    
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message4')) }} />
                    
                    {/* Core Equation */}
            <div className="bg-primary text-white p-6 rounded-lg text-center font-bold text-lg md:text-xl">
                        {getText('home.president.message5') || "Strong Industries = Strong Region = Strong Nation"}
                    </div>
                    
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message6')) }} />
                    
            {/* Commitment Section */}
            <div className="mt-8">
              <h3 className="text-lg md:text-xl font-bold text-navy-deep mb-3">
                {getText('about.president.purpose.title')}
              </h3>
              <p className="text-base font-medium text-navy-deep/80 mb-4">
                {getText('about.president.purpose.committed')}
              </p>
                      
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {commitmentItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-navy-deep/10 hover:border-primary/40 hover:shadow-md transition">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-0.5">
                              <span className="text-white font-bold text-xs">✓</span>
                            </div>
                    <p className="text-sm text-navy-deep/80 leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Platform Statement */}
            <div className="bg-primary/5 border-l-4 border-primary p-5 rounded-r-lg">
              <p className="text-base font-medium text-navy-deep mb-1"
                dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message8')?.split('—')[0]) }} />
              <p className="text-base font-medium text-primary"
                dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message8')?.split('—')[1]) }} />
                    </div>
                    
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message8')?.split('.')[0]) }} />
            <p dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.message9')?.split('.')[0]) }} />
                    
                    {/* Thank You */}
            <div className="bg-background-light border-l-4 border-primary p-5 rounded-r-lg">
              <p className="text-base font-medium text-navy-deep"
                dangerouslySetInnerHTML={{ __html: formatText(getText('home.president.thankYou')) }} />
                    </div>

                  </div>

                  {/* Signature */}
          <div className="pt-8 mt-8 border-t border-navy-deep/10">
                    <div className="flex flex-col items-end text-right space-y-1">
              <p className="text-xl md:text-2xl font-bold text-navy-deep">
                {getText('home.president.name')}
              </p>
              <p className="text-base md:text-lg text-primary font-semibold">
                {getText('home.president.position')}
              </p>
                    </div>
                  </div>

                </div>
              </div>

              {/* Government Partnerships */}
      <div className="bg-white rounded-xl border border-navy-deep/10 p-6 md:p-8 mb-10 shadow-sm">
        <div className="h-1 bg-primary mb-6"></div>
                
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-navy-deep/10">
          <div className="bg-primary p-2 rounded-lg text-white">
            <Building2 className="h-6 w-6" />
                  </div>
          <h3 className="text-xl md:text-2xl font-bold text-navy-deep">
                    {getText('about.president.partnerships.title')}
                  </h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {governmentDepts.map((dept, index) => (
            <div key={index} className="bg-white rounded-lg p-4 text-center border border-navy-deep/10 hover:border-primary/40 hover:shadow-md transition">
                      <div className="text-3xl mb-2">{dept.icon}</div>
                      <p className="text-sm font-semibold text-gray-800">{dept.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Experience Timeline */}
      <div className="bg-white rounded-xl border border-navy-deep/10 p-6 md:p-8 shadow-sm">
        <div className="h-1 bg-primary mb-6"></div>
                
                <div className="flex items-center gap-3 mb-8 pb-5 border-b-2 border-gradient-to-r from-primary-200 to-primary-100">
                  <div className="bg-gradient-to-br from-primary-600 to-primary-700 p-2 rounded-lg shadow-md">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
          <h3 className="text-xl md:text-2xl font-bold text-navy-deep">
                    {getText('about.president.timeline.title') || "Professional Journey"}
                  </h3>
                </div>
                <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-px bg-primary/20"></div>
                  
                  <div className="space-y-8">
                    {timelineData.map((item, index) => (
                      <div key={index} className="relative flex gap-6">
                        <div className="flex-shrink-0 relative z-10">
                  <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center text-white font-bold border-4 border-white shadow">
                    {item.year.split(' ')[0]}
                          </div>
                        </div>
                        
                        <div className="flex-1 pb-8 last:pb-0">
                  <div className="bg-white rounded-lg p-5 border-l-4 border-primary hover:shadow-md transition">
                    <h4 className="text-lg md:text-xl font-bold text-navy-deep mb-1">
                      {item.title}
                    </h4>
                    <p className="text-sm text-primary font-semibold mb-3">
                      {item.year}
                    </p>
                    <p className="text-sm md:text-base text-navy-deep/80 leading-relaxed">
                      {item.desc}
                    </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>


    </div>
  );
};

export default PresidentPage;
